import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { CARD_RATIO, sizedDeckImage } from '@lunary/app-core/deck-model'
import { CardFx } from './card-fx'
import { CardGlow } from './card-glow'
import { useCardBack, useCardFace } from './deck'
import { FlipBack } from './flip-backs'
import { FLIP_VARIANTS, GLOW_COUNT, randomIndex, scaleBurst } from '@lunary/app-core/flip-variants'
import { FACE_WAIT_MS, REVEAL_TIMINGS } from './flip-timing'
import { toRoman } from '@lunary/app-core/roman'
import styles from './flip-card.module.css'

type Phase = 'closed' | 'wait' | 'go' | 'open' | 'done'

const BOX_RATIO = 1 / CARD_RATIO

function fitBox(ratio: number): CSSProperties {
  return ratio >= BOX_RATIO
    ? { width: '100%', height: `${(BOX_RATIO / ratio) * 100}%` }
    : { width: `${(ratio / BOX_RATIO) * 100}%`, height: '100%' }
}

type Props = {
  number: number
  name: string
  open: boolean
  width?: number
  faceSrc?: string | null
  variant?: number
  glow?: boolean
  glowBackdrop?: boolean
  label?: string
  onOpen?: () => void
  onRevealed?: () => void
}

export function FlipCard({ number, name, open, width = 180, faceSrc, variant: fixedVariant, glow: showGlow = true, glowBackdrop = true, label, onOpen, onRevealed }: Props) {
  const [randomVariant] = useState(() => randomIndex(FLIP_VARIANTS.length))
  const variant = fixedVariant ?? randomVariant
  const [glow] = useState(() => randomIndex(GLOW_COUNT))
  const [initiallyOpen] = useState(open)
  const [step, setStep] = useState<Phase>('closed')
  const [decoded, setDecoded] = useState<string | null>(null)
  const [art, setArt] = useState<{ src: string; ratio: number } | null>(null)
  const [backArt, setBackArt] = useState<{ src: string; ratio: number } | null>(null)
  const [waited, setWaited] = useState(false)
  const [lit, setLit] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const revealedRef = useRef(onRevealed)

  useEffect(() => {
    revealedRef.current = onRevealed
  }, [onRevealed])
  const deckBack = useCardBack()
  const deckFace = useCardFace(number)
  const chosen = faceSrc === undefined ? deckFace : faceSrc
  const src = chosen ? sizedDeckImage(chosen, width) : chosen
  const launch = open && !initiallyOpen && (!src || decoded === src || waited)
  const phase: Phase = initiallyOpen ? 'done' : !open ? 'closed' : step !== 'closed' ? step : launch ? 'go' : 'wait'

  useEffect(() => {
    if (!src) return
    let alive = true
    const img = new Image()
    img.decoding = 'async'
    img.src = src
    const ready = () => {
      if (!alive) return
      if (img.naturalWidth > 0 && img.naturalHeight > 0) setArt({ src, ratio: img.naturalWidth / img.naturalHeight })
      setDecoded(src)
    }
    img.decode().then(ready, ready)
    return () => {
      alive = false
    }
  }, [src])

  useEffect(() => {
    if (!open || initiallyOpen) return
    const timer = window.setTimeout(() => setWaited(true), FACE_WAIT_MS)
    return () => window.clearTimeout(timer)
  }, [open, initiallyOpen])

  useEffect(() => {
    if (!launch) return
    const spec = REVEAL_TIMINGS[variant]
    if (!spec) return
    const fx = canvasRef.current ? new CardFx(canvasRef.current) : null
    const k = width / 180
    const timers = [
      window.setTimeout(() => setStep('open'), spec.flipAt),
      window.setTimeout(() => setLit(true), spec.glowAt),
      ...spec.fx.map(([ms, bursts]) => window.setTimeout(() => bursts.forEach((b) => fx?.burst(scaleBurst(b, k))), ms)),
      window.setTimeout(() => {
        setStep('done')
        revealedRef.current?.()
      }, spec.doneAt),
    ]
    return () => {
      timers.forEach((t) => window.clearTimeout(t))
      fx?.destroy()
    }
  }, [launch, variant, width])

  const classes = [
    styles.flip,
    styles[`v${variant}`],
    phase === 'wait' ? styles.wait : '',
    phase !== 'closed' && phase !== 'wait' ? styles.go : '',
    phase === 'open' || phase === 'done' ? styles.open : '',
    phase === 'done' ? styles.done : '',
    lit ? styles.lit : '',
    initiallyOpen ? styles.static : '',
    !open && onOpen ? styles.tappable : '',
  ].join(' ')
  const style = { '--w': `${width}px` } as CSSProperties
  const backSrc = deckBack.kind === 'image' ? sizedDeckImage(deckBack.src, width) : null
  const faceRatio = art && art.src === src ? art.ratio : null
  const backRatio = backArt && backArt.src === backSrc ? backArt.ratio : null
  const boxRatio = faceRatio ?? backRatio
  const boxStyle = boxRatio ? fitBox(boxRatio) : undefined
  const face = src ? (
    <img src={src} alt={name} width={width} height={Math.round(width * CARD_RATIO)} draggable={false} />
  ) : (
    <div className={styles.frontFallback}>
      <span>{toRoman(number)}</span>
      <span>{name}</span>
    </div>
  )

  return (
    <button
      type="button"
      className={classes}
      style={style}
      onClick={!open ? onOpen : undefined}
      aria-label={open ? name : (label ?? 'Открыть карту')}
      aria-disabled={open || !onOpen}
    >
      {(phase === 'done' || lit) && showGlow && <CardGlow variant={glow} width={width} backdrop={glowBackdrop} />}
      <span className={styles.bridge} />
      <span className={styles.halo} />
      <span className={styles.rays} />
      <span className={styles.mist} />
      <span className={styles.float}>
        <span className={styles.lift}>
          <span className={styles.scene}>
            <span className={styles.card}>
              <span className={`${styles.face} ${styles.back} ${backSrc ? styles.imageBack : ''}`} style={backSrc ? undefined : boxStyle}>
                {backSrc ? (
                  <span className={`${styles.faceArt} ${boxStyle ? styles.fitted : ''}`} style={boxStyle}>
                    <img
                      className={styles.deckBack}
                      src={backSrc}
                      alt=""
                      decoding="async"
                      draggable={false}
                      onLoad={(e) => {
                        const { naturalWidth, naturalHeight } = e.currentTarget
                        if (naturalWidth > 0 && naturalHeight > 0) setBackArt({ src: backSrc, ratio: naturalWidth / naturalHeight })
                      }}
                    />
                  </span>
                ) : (
                  <FlipBack variant={variant} />
                )}
              </span>
              <span className={`${styles.face} ${styles.front}`}>
                <span className={`${styles.faceArt} ${boxStyle ? styles.fitted : ''}`} style={boxStyle}>
                  {face}
                  <span className={styles.sheen} />
                </span>
              </span>
            </span>
          </span>
        </span>
      </span>
      <span className={styles.ring} />
      <span className={styles.flash} />
      <canvas ref={canvasRef} className={styles.fx} aria-hidden="true" />
    </button>
  )
}
